import PropTypes from 'prop-types';
import React from 'react';

import ResponsiveImage from 'components/ResponsiveImage';
import VideoPlayer from 'components/VideoPlayer';
import { IMAGE_WIDTHS } from 'constants/images';
import { cn } from 'utils/Tailwind';

export const OVERLAY_POSITIONS = {
  TOP: 'Top',
  CENTER: 'Center',
};

function PageSectionDualPanelModule({ items, isInverted, overlayPosition, trackCTAClick }) {
  return (
    <section
      className={cn('PageSectionDualPanelModule mt-2 grid gap-2 md:grid-cols-2', {
        'text-white': isInverted,
      })}
    >
      {items?.map(({ title, subtitle, eyebrow, ctaLinks, asset }, index) => (
        <DualPanelModule
          key={index}
          asset={asset}
          ctaLinks={ctaLinks}
          eyebrow={eyebrow}
          index={index}
          isInverted={isInverted}
          overlayPosition={overlayPosition}
          subtitle={subtitle}
          title={title}
          trackCTAClick={trackCTAClick}
        />
      ))}
    </section>
  );
}

function DualPanelModule({
  asset,
  ctaLinks,
  eyebrow,
  isInverted,
  overlayPosition,
  subtitle,
  title,
  trackCTAClick,
}) {
  return (
    <div className="group/card relative overflow-hidden">
      {asset.file.contentType?.includes('video') ? (
        <VideoPlayer loop muted playing={true} playsinline url={asset.file.url} />
      ) : (
        <ResponsiveImage
          className="transition-transform duration-300 ease-in-out lg:group-hover/card:scale-105"
          src={asset.file.url}
          width={IMAGE_WIDTHS.W_1000}
          height={IMAGE_WIDTHS.W_1000}
          lazy
        />
      )}

      <ModuleContent
        ctaLinks={ctaLinks}
        eyebrow={eyebrow}
        isInverted={isInverted}
        overlayPosition={overlayPosition}
        subtitle={subtitle}
        title={title}
        trackCTAClick={trackCTAClick}
      />
    </div>
  );
}

function ModuleContent({
  ctaLinks,
  eyebrow,
  isInverted,
  overlayPosition,
  subtitle,
  title,
  trackCTAClick,
}) {
  return (
    <div
      className={cn(
        `absolute inset-0 flex h-full flex-col items-center justify-center gap-2 bg-black/20 p-6 text-center md:px-8 md:py-16`,
        {
          'justify-start': overlayPosition === OVERLAY_POSITIONS.TOP,
        }
      )}
    >
      {eyebrow && <p className="text-sm font-medium uppercase tracking-widest">{eyebrow}</p>}
      {title && <h2 className="text-lg/6 font-bold md:text-2xl/7">{title}</h2>}
      {subtitle && <p className="text-sm sm:text-base">{subtitle}</p>}

      {!!ctaLinks.length && (
        <div
          className={cn(
            `mt-2 flex w-full flex-wrap justify-center gap-2 px-6 transition-opacity duration-300 ease-in-out focus-within:opacity-100 lg:opacity-0 lg:group-hover/card:opacity-100`,
            {
              'absolute bottom-6 lg:bottom-10': overlayPosition === OVERLAY_POSITIONS.TOP,
            }
          )}
        >
          {ctaLinks.map(({ url, text }) => (
            <a
              href={url}
              className={cn(
                `flex-1 whitespace-pre border bg-transparent bg-white p-3.5 text-center text-xs font-medium uppercase tracking-widest text-black transition-colors duration-300 ease-in-out hover:bg-black hover:text-white md:min-w-40 md:flex-none`,
                [!isInverted && 'text-black']
              )}
              key={text}
              onClick={() => trackCTAClick({ text, url })}
            >
              {text}
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

PageSectionDualPanelModule.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      asset: PropTypes.shape({
        description: PropTypes.string,
        file: PropTypes.shape({
          url: PropTypes.string,
          contentType: PropTypes.string,
        }),
      }),
      ctaLinks: PropTypes.arrayOf(
        PropTypes.shape({
          url: PropTypes.string,
          text: PropTypes.string,
        })
      ),
      eyebrow: PropTypes.string,
      title: PropTypes.string,
      internalName: PropTypes.string,
      subtitle: PropTypes.string,
    })
  ),
  isInverted: PropTypes.bool,
  overlayPosition: PropTypes.oneOf(Object.values(OVERLAY_POSITIONS)),
  trackCTAClick: PropTypes.func,
};

PageSectionDualPanelModule.defaultProps = {
  items: [],
  isInverted: true,
  overlayPosition: OVERLAY_POSITIONS.CENTER,
};

DualPanelModule.propTypes = {
  asset: PropTypes.shape({
    file: PropTypes.shape({
      contentType: PropTypes.string,
      url: PropTypes.string,
    }),
  }),
  ctaLinks: PropTypes.arrayOf(
    PropTypes.shape({
      url: PropTypes.string,
      text: PropTypes.string,
    })
  ),
  eyebrow: PropTypes.string,
  isInverted: PropTypes.bool,
  overlayPosition: PropTypes.oneOf(Object.values(OVERLAY_POSITIONS)),
  subtitle: PropTypes.string,
  title: PropTypes.string,
  trackCTAClick: PropTypes.func,
};

DualPanelModule.defaultProps = {
  asset: {
    file: {
      contentType: '',
      url: '',
    },
  },
  ctaLinks: [],
  eyebrow: '',
  isInverted: true,
  overlayPosition: OVERLAY_POSITIONS.CENTER,
  subtitle: '',
  title: '',
};

ModuleContent.propTypes = {
  eyebrow: PropTypes.string,
  title: PropTypes.string,
  internalName: PropTypes.string,
  overlayPosition: PropTypes.oneOf(Object.values(OVERLAY_POSITIONS)),
  subtitle: PropTypes.string,
  ctaLinks: PropTypes.arrayOf(
    PropTypes.shape({
      url: PropTypes.string,
      text: PropTypes.string,
    })
  ),
  isInverted: PropTypes.bool,
  trackCTAClick: PropTypes.func,
};

ModuleContent.defaultProps = {
  ctaLinks: [],
  eyebrow: '',
  isInverted: true,
  overlayPosition: OVERLAY_POSITIONS.CENTER,
  subtitle: '',
  title: '',
};

export default PageSectionDualPanelModule;
